import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiXCircle, FiAlertCircle, FiRefreshCw, FiDatabase, FiMail, FiCpu, FiPhone, FiHardDrive, FiServer } from 'react-icons/fi';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import { tw, monoFont } from '../utils/terminalTheme';

interface ServiceStatus {
  status: 'healthy' | 'error' | 'not_configured' | 'unknown' | 'degraded';
  [key: string]: any;
}

interface SystemStatusData {
  timestamp: string;
  overall_status: string;
  services: {
    database: ServiceStatus;
    sendgrid: ServiceStatus;
    ai_provider: ServiceStatus;
    openphone: ServiceStatus;
    file_storage: ServiceStatus;
    redis: ServiceStatus;
  };
  environment: {
    railway_environment: string;
    deployment_version: string;
  };
}

export const SystemStatus: React.FC = () => {
  const [status, setStatus] = useState<SystemStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/system/status');
      setStatus(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      toast.error('Failed to fetch system status');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <FiCheckCircle className="w-5 h-5 text-green-400" />;
      case 'error':
        return <FiXCircle className="w-5 h-5 text-red-400" />;
      case 'not_configured':
        return <FiAlertCircle className="w-5 h-5 text-gray-500" />;
      default:
        return <FiAlertCircle className="w-5 h-5 text-amber-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    let colors = 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    switch (status) {
      case 'healthy':
        colors = 'bg-green-500/15 text-green-400 border-green-500/30';
        break;
      case 'error':
        colors = 'bg-red-500/15 text-red-400 border-red-500/30';
        break;
      case 'not_configured':
        colors = 'bg-gray-500/15 text-gray-400 border-gray-500/30';
        break;
    }
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono border ${colors}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'database': return <FiDatabase className="w-4 h-4" />;
      case 'sendgrid': return <FiMail className="w-4 h-4" />;
      case 'ai_provider': return <FiCpu className="w-4 h-4" />;
      case 'openphone': return <FiPhone className="w-4 h-4" />;
      case 'file_storage': return <FiHardDrive className="w-4 h-4" />;
      case 'redis': return <FiServer className="w-4 h-4" />;
      default: return <FiServer className="w-4 h-4" />;
    }
  };

  const getServiceName = (service: string) => {
    switch (service) {
      case 'database': return 'Database';
      case 'sendgrid': return 'SendGrid Email';
      case 'ai_provider': return 'AI Processing';
      case 'openphone': return 'OpenPhone';
      case 'file_storage': return 'File Storage';
      case 'redis': return 'Redis Cache';
      default: return service;
    }
  };

  if (isLoading && !status) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>system/<span className="text-cyan-400">status</span>
            <span className="text-gray-600 text-sm font-normal ml-3">
              <span className="text-green-500">$</span> healthcheck --all
            </span>
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            Monitor the health of all integrated services
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={isLoading}
          className={`${tw.btnPrimary} flex items-center gap-2 ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <FiRefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      {status && (
        <div className={`bg-gray-800/80 border rounded-lg p-5 mb-6 flex items-center justify-between ${
          status.overall_status === 'healthy'
            ? 'border-green-500/30'
            : 'border-amber-500/30'
        }`}>
          <div className="flex items-center gap-3">
            {status.overall_status === 'healthy' ? (
              <FiCheckCircle className="w-6 h-6 text-green-400" />
            ) : (
              <FiAlertCircle className="w-6 h-6 text-amber-400" />
            )}
            <div>
              <h2 className="text-sm font-mono font-medium text-white">
                System {status.overall_status === 'healthy' ? 'Operational' : 'Degraded'}
              </h2>
              <p className="text-[10px] text-gray-500 font-mono">
                Last updated: {new Date(status.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500 font-mono">
              ENV: <span className="text-gray-400">{status.environment.railway_environment}</span>
            </p>
            <p className="text-[10px] text-gray-500 font-mono">
              VER: <span className="text-gray-400">{status.environment.deployment_version.substring(0, 8)}</span>
            </p>
          </div>
        </div>
      )}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {status && Object.entries(status.services).map(([serviceName, serviceData]) => (
          <div
            key={serviceName}
            className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5 hover:border-cyan-500/30 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gray-900 rounded border border-gray-700/50 text-gray-400">
                  {getServiceIcon(serviceName)}
                </div>
                <h3 className="text-sm font-mono font-medium text-white">
                  {getServiceName(serviceName)}
                </h3>
              </div>
              {getStatusIcon(serviceData.status)}
            </div>

            <div className="mb-3">
              {getStatusBadge(serviceData.status)}
            </div>

            {/* Service-specific details */}
            <div className="space-y-1.5 text-xs font-mono">
              {serviceName === 'database' && serviceData.stats && (
                <>
                  <p className="text-gray-500">
                    Documents: <span className="text-gray-300">{serviceData.stats.total_documents}</span>
                  </p>
                  <p className="text-gray-500">
                    Last 24h: <span className="text-gray-300">{serviceData.stats.documents_last_24h}</span>
                  </p>
                  {serviceData.stats.failed_documents > 0 && (
                    <p className="text-red-400">
                      Failed: <span className="text-red-300">{serviceData.stats.failed_documents}</span>
                    </p>
                  )}
                </>
              )}

              {serviceName === 'ai_provider' && (
                <>
                  <p className="text-gray-500">
                    Provider: <span className="text-gray-300">{serviceData.provider}</span>
                  </p>
                  {serviceData.openai_configured && (
                    <p className="text-gray-500">OpenAI: <span className="text-green-400">Configured</span></p>
                  )}
                  {serviceData.anthropic_configured && (
                    <p className="text-gray-500">Anthropic: <span className="text-green-400">Configured</span></p>
                  )}
                  {serviceData.pdf2image_available !== undefined && (
                    <p className="text-gray-500">
                      PDF to Image: <span className={serviceData.pdf2image_available ? 'text-green-400' : 'text-amber-400'}>
                        {serviceData.pdf2image_available ? 'Available' : 'Not Available'}
                      </span>
                    </p>
                  )}
                  {serviceData.pdf2image_note && (
                    <p className="text-amber-400/70 text-[10px] mt-1">{serviceData.pdf2image_note}</p>
                  )}
                  {serviceData.test_cost_note && (
                    <p className="text-gray-600 text-[10px] mt-1">{serviceData.test_cost_note}</p>
                  )}
                </>
              )}

              {serviceName === 'file_storage' && (
                <>
                  <p className="text-gray-500">
                    Upload Dir: <span className={serviceData.upload_dir_exists ? 'text-green-400' : 'text-red-400'}>
                      {serviceData.upload_dir_exists ? 'Exists' : 'Missing'}
                    </span>
                  </p>
                  <p className="text-gray-500">
                    Writable: <span className={serviceData.writable ? 'text-green-400' : 'text-red-400'}>
                      {serviceData.writable ? 'Yes' : 'No'}
                    </span>
                  </p>
                  {serviceData.railway_volume && (
                    <p className="text-gray-500">
                      Railway Volume: <span className={serviceData.railway_volume_exists ? 'text-green-400' : 'text-red-400'}>
                        {serviceData.railway_volume_exists ? 'Mounted' : 'Not Mounted'}
                      </span>
                    </p>
                  )}
                </>
              )}

              {serviceName === 'openphone' && serviceData.messages_last_7_days !== undefined && (
                <p className="text-gray-500">
                  Messages (7d): <span className="text-gray-300">{serviceData.messages_last_7_days}</span>
                </p>
              )}

              {serviceData.error && (
                <p className="text-red-400 break-all text-[10px]">
                  Error: {serviceData.error}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Auto-refresh indicator */}
      <div className="mt-6 text-center text-[10px] text-gray-600 font-mono">
        Auto-refreshing every 30 seconds &middot; Last refresh: {lastRefresh.toLocaleTimeString()}
      </div>
    </div>
  );
};
